/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {Text, Box} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {isAbsoluteURL} from '@salesforce/retail-react-app/app/page-designer/utils'
import useSegment from '../../../integrations/segment/app/hooks/use-segment'

/**
 * This layout component displays its children in a 2 row x 1 column grid on mobile
 * and a 1 row x 2 column grid on desktop.
 *
 * @param {componentProps} props
 * @param {regionType []} props.regions - The page designer regions for this component.
 * @param {object} props.data - The data for the component.
 * @param {string} props.typeId - A mapping of typeId's to react components representing the type.
 */

export const buttonColor = (buttontype) => {
    // Split the button value by '_'
    const parts = buttontype.split('_')

    // Take the last part and convert it to lowercase
    const color = parts[parts.length - 1].toLowerCase()

    return color
}

export const LoaltyProgram = ({
    ITCLink,
    custom,
    textHeading,
    buttontype,
    backgroundColor,
    buttontext,
    textParagraph,
    buttonlink
}) => {
    const hasCaption = textHeading
    const isAbsolute = isAbsoluteURL(ITCLink)
    const LinkWrapper = isAbsolute ? '' : Link
    const linkProps = isAbsolute ? {href: ITCLink} : {to: ITCLink}
    const segment = useSegment()

    return (
        <Box className={'loaylty'} px="4" py="10" backgroundColor={backgroundColor}>
            <Box>
                <picture>
                    <source srcSet={custom?.imageDesktop?.disBaseLink} media="(min-width: 48em)" />
                    <source srcSet={custom?.imageDesktop?.disBaseLink} media="(min-width: 64em)" />
                    <LinkWrapper
                        {...linkProps}
                        onClick={() => {
                            // segment
                            segment.sendPanelSelectedEvent(event, {})
                        }}
                    >
                        <img
                            alt={custom?.imageDesktop?.disBaseLink}
                            className={'loaylty-image'}
                            data-testid={'loaylty-image'}
                            src={custom?.imageDesktop?.disBaseLink}
                            filter={'brightness(40%)'}
                            width="100%"
                        />
                    </LinkWrapper>
                </picture>
            </Box>
            <Box>
                {hasCaption && (
                    <Text as="figcaption">
                        {textHeading && (
                            <Box>
                                <Text as="span" fontSize="5xl" color="#0C9DDA" py="4">
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: textHeading
                                        }}
                                    />
                                </Text>
                                <Text as="span" fontSize="2xl">
                                    {/* The `dangerouslySetInnerHTML` is safe to use in this context. */}
                                    {/* The HTML in the response from Page Designer API is already sanitized. */}
                                    <Box
                                        dangerouslySetInnerHTML={{
                                            __html: textParagraph
                                        }}
                                    />
                                </Text>
                            </Box>
                        )}
                        {buttontext && (
                            <Link href={buttonlink} color={buttonColor(buttontype)}>
                                {buttontext}
                            </Link>
                        )}
                    </Text>
                )}
            </Box>
        </Box>
    )
}

LoaltyProgram.displayName = 'LoaltyProgram'

LoaltyProgram.propTypes = {
    custom: PropTypes.object,
    imageAltTag: PropTypes.string,
    buttonlink: PropTypes.string,
    textParagraph: PropTypes.string,
    buttontext: PropTypes.string,
    textHeading: PropTypes.string,
    backgroundColor: PropTypes.string,
    imageConfiguration: PropTypes.string,
    buttontype: PropTypes.string,
    showFullWidthArtical: PropTypes.bool,
    ITCLink: PropTypes.string
}

export default LoaltyProgram
